import { useState, useEffect, useRef } from 'react';
import moment from 'moment';

const usePersistentBillingFilters = (invoiceType, initialFilters = {}) => {
    const storageKey = `billing_filters_${invoiceType}`;

    const getInitialState = () => {
        try {
            const stored = sessionStorage.getItem(storageKey);
            if (stored) {
                const parsed = JSON.parse(stored);
                return parsed;
            }
        } catch (error) {
            console.error('Error reading from sessionStorage:', error);
        }

        return {
            start_date: moment().format('YYYY-MM-DD'),
            end_date: moment().format('YYYY-MM-DD'),
            filters: {
                document_id: '',
                file_number: '',
                created_by: '',
                payment_method: '',
                invoice_number: '',
                department_id: '',
                receipt_number: '',
                episode_id: '',
            },
            query: '',
            page: 1,
            per_page: 20,
            ...initialFilters,
        };
    };

    const initialStateRef = useRef(null);
    if (!initialStateRef.current) initialStateRef.current = getInitialState();

    const [filters, setDraftFilters] = useState(initialStateRef.current);
    const [appliedFilters, setAppliedFilters] = useState(initialStateRef.current);
    const filtersRef = useRef(initialStateRef.current);

    const setFilters = (updater) => {
        const previous = filtersRef.current;
        const next = typeof updater === 'function' ? updater(previous) : updater;
        const withoutPagination = value => {
            const { page, per_page, perPage, ...rest } = value || {};
            return rest;
        };

        filtersRef.current = next;
        setDraftFilters(next);

        // Pagination remains immediate, but editing any actual filter is
        // kept as a draft until the Apply button is clicked.
        if (JSON.stringify(withoutPagination(previous)) === JSON.stringify(withoutPagination(next))) {
            setAppliedFilters(current => ({
                ...current,
                page: next.page,
                per_page: next.per_page,
                perPage: next.perPage
            }));
        }
    };

    const applyFilters = (nextFilters) => {
        const next = nextFilters || filtersRef.current;
        filtersRef.current = next;
        setDraftFilters(next);
        setAppliedFilters(next);
    };

    useEffect(() => {
        try {
            sessionStorage.setItem(storageKey, JSON.stringify(appliedFilters));
        } catch (error) {
            console.error('Error saving to sessionStorage:', error);
        }
    }, [appliedFilters, storageKey]);

    const resetFilters = () => {
        const defaultFilters = {
            start_date: moment().format('YYYY-MM-DD'),
            end_date: moment().format('YYYY-MM-DD'),
            document_id: '',
            file_number: '',
            filters: {
                created_by: '',
                payment_method: '',
                invoice_number: '',
                department_id: '',
                document_id: '',
                receipt_number: '',
                episode_id: '',
            },
            query: '',
            page: 1,
            per_page: filtersRef.current.per_page || 20,
            ...initialFilters,
        };
        filtersRef.current = defaultFilters;
        setDraftFilters(defaultFilters);
        setAppliedFilters(defaultFilters);
        try {
            sessionStorage.removeItem(storageKey);
        } catch (error) {
            console.error('Error removing from sessionStorage:', error);
        }
        return defaultFilters;
    };

    return [filters, setFilters, resetFilters, appliedFilters, applyFilters];
};

export default usePersistentBillingFilters;
